import deepEqual from 'deep-equal';
import cloneDeep from 'lodash.clonedeep';
import findIndex from 'lodash.findindex';
import orderBy from 'lodash.orderby';
import takeRight from 'lodash.takeright';

import shallowequal from 'shallowequal';
import { notifyMessageSentGroupChat } from '../actions/conversationInboxAction';
import * as chatStateTypes from '../constants/chatStateTypes';
import { TYPE_GROUP_CHAT } from '../constants/chatTypes';
import { APP_SETTINGS } from '../constants/config';
import { HISTORY_DIRECTION_TYPE, MSG_TEMPLATE as msgTpl, PAGING_DEFAULT, THREE_MONTHS } from '../constants/constants';
import * as messageStatus from '../constants/messageStatus';
import { MessageStatusType } from '../constants/messageStatus';
import * as messageTypes from '../constants/messageTypes';
import {
  TYPE_IMAGE,
  TYPE_JOINED_GROUP_CHAT,
  TYPE_LEAVE_GROUP_CHAT,
  TYPE_PRIVATE_MSG,
  TYPE_REMOVED_FROM_GROUP,
  TYPE_VIDEO_FILE,
} from '../constants/messageTypes';
import { IMessage } from '../interfaces/IMessage';
import config from '../libs/webIMConfig';
import { default as chatMessage, default as ChatMessage } from '../models/chatMessage';
import { MEDIA_TYPE } from '../models/recentlySentPhotoModel';
import { default as User, default as userModel } from '../models/user';
import * as myStore from '../store/myStore';
import * as messageUtils from './messageUtils';
import { cleanObjectKeys, copyMessage as copy, hasOwnProperty, isCurrentDate, isImageExpired } from './utils';

interface IMessageReducer {
  byId?: Record<string, ChatMessage>;
  groupchat?: Record<string, string[]>;
  heartMsg?: Record<string, string[]>;
}

interface Room {
  roomJid: string;
}

interface IAdvanceParams {
  takeAllMessages?: boolean;
  takeStore?: boolean;
  revert?: boolean;
}

export function canEditMessage(message: ChatMessage | null) {
  return message && message.bySelf && isCurrentDate(message.time) && message.body?.type === messageTypes.TYPE_TEXT;
}

export function parseGifMessage(gif: any): any {
  const message: any = {};
  try {
    message.fixedWidth = gif.images.fixed_width.url || '';
    message.original = gif.images.original.url || '';
  } catch (e) {
    console.log('Can not parse Gif Message', gif);
  }
  return message;
}

/** *
 * This is a function to get all message of peerVcard
 * @param messageXmppObj //this is message object of xmpp format
 * @param peerVcard
 * @param oldestMessageTime
 * @param direction
 * @param roomMembers
 * @param advanceParams
 */
export function getMessagesByUser(
  messageXmppObj: any | undefined,
  peerVcard?: { xmppId: string; isGroup?: boolean },
  oldestMessageTime: number = 0,
  direction: number = HISTORY_DIRECTION_TYPE.FORWARD,
  roomMembers: any[] = [],
  advanceParams?: IAdvanceParams
): any[] {
  const { takeAllMessages = '', takeStore = '' } = advanceParams ? advanceParams : {};
  const messageObj = Object.assign({}, takeStore ? myStore.getStore().getState().messageReducer : messageXmppObj);
  const messages: any[] = [];
  const { dataDetailEvent } = myStore.getStore().getState().eventReducer;
  let groupChatRoomMembersData: { [key: string]: any } = {};
  if (peerVcard?.isGroup) {
    const listParticipantView =
      dataDetailEvent && dataDetailEvent.groupChatDetail && dataDetailEvent.groupChatDetail.roomMembers
        ? dataDetailEvent.groupChatDetail.roomMembers
        : [];
    const listInActiveRoomMembers =
      dataDetailEvent && dataDetailEvent.groupChatDetail && dataDetailEvent.groupChatDetail.inActiveRoomMembers
        ? dataDetailEvent.groupChatDetail.inActiveRoomMembers
        : [];
    const listPendingRoomMembers =
      dataDetailEvent && dataDetailEvent.groupChatDetail && dataDetailEvent.groupChatDetail.pendingRoomMembers
        ? dataDetailEvent.groupChatDetail.pendingRoomMembers
        : [];
    const { usersReducer } = myStore.getStore().getState();
    let data = [...listParticipantView, ...listInActiveRoomMembers, ...listPendingRoomMembers, ...(usersReducer.users || [])];
    if (data?.length > 0) {
      data.forEach(item => {
        groupChatRoomMembersData[item.userId] = item;
      });
    }
  }
  if (messageObj && messageObj.byId && peerVcard && peerVcard.xmppId) {
    if (hasChatMessage(messageObj.byId)) {
      const msgIds = peerVcard.isGroup ? messageObj.groupchat[peerVcard.xmppId] : messageObj.chat[peerVcard.xmppId];
      const loginUser = myStore.getStore().getState().loginReducer.userInfo;
      if (msgIds) {
        msgIds.forEach((msgId: string) => {
          const message = messageObj.byId[msgId];

          if (
            (direction === HISTORY_DIRECTION_TYPE.FORWARD && message && message.time >= oldestMessageTime) ||
            (direction === HISTORY_DIRECTION_TYPE.BACKWARD && message && message.time < oldestMessageTime)
          ) {
            const msgModel = convertMsgXmppToChatMessageModel(message);
            if (!peerVcard.isGroup) {
              if (msgModel.to === peerVcard.xmppId) {
                msgModel.user = loginUser;
              } else {
                msgModel.user = peerVcard as User;
              }
            } else {
              const user = groupChatRoomMembersData[message.from];
              if (msgModel.refUserId && msgModel.refUserId !== -1) {
                const userRef = groupChatRoomMembersData[msgModel.refUserId];
                if (userRef) {
                  msgModel.refDisplayName = userRef.displayName;
                }
              }
              if (user) {
                msgModel.user = user;
              }
              if (isGroupMessageStatus(msgModel)) {
                let str = msgModel.body.data.value || '';
                let displayName = str.substring(0, str.indexOf(' '));

                msgModel.body.data.userName = displayName;
                // use displayName in web
                msgModel.body.data.displayName = displayName;
              }
            }
            messages.push(msgModel);
          }
        });
      }
    }
  }
  if (messages.length > 0) {
    const messageSorted = orderBy(messages, (m: any) => m.time, 'asc');
    if (takeAllMessages) {
      return messageSorted;
    }
    // only get last 50 messages if oldestMessageTime is not present or 0
    if (oldestMessageTime === 0 && direction === HISTORY_DIRECTION_TYPE.FORWARD) {
      return takeRight(messageSorted, APP_SETTINGS.INITIAL_CONV_DETAIL_MSG_COUNT);
    } else if (direction === HISTORY_DIRECTION_TYPE.BACKWARD) {
      return takeRight(messageSorted, PAGING_DEFAULT.PAGE_SIZE_MESSAGE);
    }
    return messageSorted;
  }
  return messages;
}

/** *
 * This is a function to improve performance get latest message of user instead using get all message and get item end of array
 * @param messageXmppObj //this is message object of xmpp format
 * @param jid //this is jid need get
 */
export function getLatestChatMessageByUser(messageXmppObj: any, jid: string): any {
  const messageObj = Object.assign({}, messageXmppObj);
  let message: any = null;
  if (messageObj && messageObj.byId && jid) {
    const msgIds = messageObj.chat[jid];
    if (msgIds && msgIds.length > 0) {
      message = convertMsgXmppToChatMessageModel(messageObj.byId[msgIds[msgIds.length - 1]]);
    }
  }
  return message;
}

/** *
 * This is a function to convert message model (API) to chatMessage model (Gift chat)
 * @param messageModel // object message model
 */
export function getChatMessageModel(messageModel: any): any {
  let message = Object.assign(new chatMessage(), messageModel);
  switch (messageModel.status) {
    case messageStatus.STATUS_SENT:
      message.sent = true;
      break;
    case messageStatus.STATUS_DELIVERED:
      message.received = true;
      break;
    case messageStatus.STATUS_UNDELIVERED:
      message.sent = true;
      message.received = false;
      break;
    case messageStatus.STATUS_READ:
      message.read = true;
      break;
    case messageStatus.STATUS_DELETED:
      message.isDelete = true;
      message.read = false;
      break;
    default:
      message.sent = false;
  }
  if (messageModel.createdAt) {
    message.time = +new Date(messageModel.createdAt);
  }
  if (messageModel.isDelete) {
    message.isDelete = messageModel.isDelete;
  }
  message.from = messageUtils.removePrefixAndSuffix(messageModel.from);
  message.to = messageUtils.removePrefixAndSuffix(messageModel.to);
  message.user = messageModel.user ? userModel.mappingObject(messageModel.user) : null;
  message = cleanObjectKeys(Object.keys(new chatMessage()), message); // clean other key doesn't exist in chatMessage model
  return message;
}

/** *
 *
 * @param messageXmppObj ==> this is byId object of xmpp (this.props.message.byId)
 */
export function getLatestXmppMessageByTime(messageXmppObj: any): any {
  if (messageXmppObj !== null && messageXmppObj.byId !== null) {
    const messageObj = Object.assign({}, messageXmppObj.byId);
    const arr = Object.keys(messageObj).filter(key => key.indexOf('WEBIM_') !== -1);
    const lastKey = (arr && arr.length > 0 && arr[arr.length - 1]) || false;
    return lastKey ? messageObj[lastKey] : null;
  }
  return null;
}

export function buildChatMessageModelfromMsgXmpp(messageXmpp: any, loginUser: any, peerUser: any, isGroup: boolean): any {
  const msgModel = convertMsgXmppToChatMessageModel(messageXmpp);
  if (isGroup) {
    msgModel.user = peerUser;
  } else {
    if (msgModel.to === peerUser.xmppId) {
      msgModel.user = loginUser;
    } else {
      msgModel.user = peerUser;
    }
  }

  return msgModel;
}

export function buildChatMessageModelFromChatState(chatState: any, loginUser: any, peerUser: any): any {
  const msgModel = new chatMessage();
  msgModel.createdAt = new Date(chatState.updatedTime);
  msgModel.user = peerUser;
  msgModel.time = chatState.updatedTime;
  msgModel.from = peerUser.xmppId.toString();
  msgModel.to = loginUser.xmppId.toString();
  msgModel.id = messageTypes.TYPE_CHAT_STATE;

  if (chatState.chatStateType === chatStateTypes.COMPOSING) {
    msgModel.body = { type: chatStateTypes.COMPOSING, data: 'Typing' };
  } else if (chatState.chatStateType === chatStateTypes.PAUSED) {
    msgModel.body = { type: chatStateTypes.PAUSED, data: '' };
  }

  return msgModel;
}

export function convertMsgXmppToChatMessageModel(messageXmpp: any): ChatMessage {
  let message = Object.assign(new chatMessage(), cloneDeep(messageXmpp));

  switch (messageXmpp?.status) {
    case messageStatus.STATUS_SENT:
      message.sent = true;
      break;
    case messageStatus.STATUS_DELIVERED:
      message.received = true;
      break;
    case messageStatus.STATUS_UNDELIVERED:
      message.sent = true;
      message.received = false;
      break;
    case messageStatus.STATUS_READ:
      message.read = true;
      break;
    case messageStatus.STATUS_DELETED:
      message.isDelete = true;
      message.read = false;
      break;
    case messageStatus.STATUS_HIDDEN:
      message.isHidden = true;
      message.read = false;
      break;
    default:
      message.sent = false;
  }

  if (messageXmpp.time) {
    message.createdAt = new Date(messageXmpp.time);
  }
  // filter message expired
  if (messageXmpp.expts) {
    const currentTs = Date.parse(new Date().toString());
    if (currentTs > messageXmpp.expts) {
      message.expts = 1;
    }
  }
  message = cleanObjectKeys(Object.keys(new chatMessage()), message); // clean other key doesn't exist in chatMessage model
  message.isParsedFromLocal = messageXmpp.isParsedFromLocal;
  if (isGroupMessageStatus(message)) {
    let str = message.body.data.value || '';
    let displayName = str.substring(0, str.indexOf(' '));

    message.body.data.userName = displayName;
    // use displayName in web
    message.body.data.displayName = displayName;
  }

  return message;
}

export function hasChatMessage(messageXmppObj: any): boolean {
  if (messageXmppObj) {
    return Object.keys(messageXmppObj).some(prop => {
      return prop.indexOf('WEBIM_') !== -1;
    });
  }
  return false;
}

export function parseFromServer(message: IMessage = { id: '' }, bodyType: string) {
  const ext = message.ext || {};
  const obj = copy(message, msgTpl.base);
  let body = copy(message, msgTpl[bodyType as keyof typeof msgTpl]);
  if (message.type === TYPE_PRIVATE_MSG) {
    body = {
      ...body,
      isPrivate: true,
      isModerator: message.isModerator || false,
    };
  }
  switch (bodyType) {
    case messageTypes.TYPE_TEXT:
      return {
        ...obj,
        status: 'sent',
        body: {
          ...body,
          ...ext,
          data: message.data,
          type: messageTypes.TYPE_TEXT,
          senderMemberLevel: message.senderMemberLevel,
          recvMemberLevel: message.recvMemberLevel,
          isRecvMatching: message.isRecvMatching,
          isSenderMatching: message.isSenderMatching,
        },
      };
    case messageTypes.TYPE_GIF:
      return {
        ...obj,
        status: 'sent',
        body: {
          ...body,
          ...ext,
          data: message.data,
          type: messageTypes.TYPE_GIF,
          senderMemberLevel: message.senderMemberLevel,
          recvMemberLevel: message.recvMemberLevel,
          isRecvMatching: message.isRecvMatching,
          isSenderMatching: message.isSenderMatching,
        },
      };
    case messageTypes.TYPE_SHARE_LOCATION:
      return {
        ...obj,
        status: 'sent',
        body: {
          ...body,
          ...ext,
          data: message.data,
          type: messageTypes.TYPE_SHARE_LOCATION,
          senderMemberLevel: message.senderMemberLevel,
          recvMemberLevel: message.recvMemberLevel,
          isRecvMatching: message.isRecvMatching,
          isSenderMatching: message.isSenderMatching,
        },
      };
    case messageTypes.TYPE_SHARE_DATE:
      return {
        ...obj,
        status: 'sent',
        body: {
          ...body,
          ...ext,
          data: message.data,
          type: messageTypes.TYPE_SHARE_DATE,
          senderMemberLevel: message.senderMemberLevel,
          recvMemberLevel: message.recvMemberLevel,
          isRecvMatching: message.isRecvMatching,
          isSenderMatching: message.isSenderMatching,
        },
      };
    case messageTypes.TYPE_SHARE_EVENT:
      return {
        ...obj,
        status: 'sent',
        body: {
          ...body,
          ...ext,
          data: message.data,
          type: messageTypes.TYPE_SHARE_EVENT,
          senderMemberLevel: message.senderMemberLevel,
          recvMemberLevel: message.recvMemberLevel,
          isRecvMatching: message.isRecvMatching,
          isSenderMatching: message.isSenderMatching,
        },
      };
    case messageTypes.TYPE_SHARE_EVENT_TICKET:
      return {
        ...obj,
        status: 'sent',
        body: {
          ...body,
          ...ext,
          data: message.data,
          type: messageTypes.TYPE_SHARE_EVENT_TICKET,
          senderMemberLevel: message.senderMemberLevel,
          recvMemberLevel: message.recvMemberLevel,
          isRecvMatching: message.isRecvMatching,
          isSenderMatching: message.isSenderMatching,
        },
      };
    case messageTypes.TYPE_TOOK_SCREEN_SHOT:
      return {
        ...obj,
        status: 'sent',
        body: {
          ...body,
          ...ext,
          data: message.data || '',
          type: messageTypes.TYPE_TOOK_SCREEN_SHOT,
          senderMemberLevel: message.senderMemberLevel,
          recvMemberLevel: message.recvMemberLevel,
          isRecvMatching: message.isRecvMatching,
          isSenderMatching: message.isSenderMatching,
        },
      };
    case messageTypes.TYPE_REMOVED_FROM_GROUP:
      return {
        ...obj,
        status: 'sent',
        body: {
          ...body,
          ...ext,
          data: message.data || '',
          type: messageTypes.TYPE_REMOVED_FROM_GROUP,
        },
      };
    case messageTypes.TYPE_LEAVE_GROUP_CHAT:
      return {
        ...obj,
        status: 'sent',
        body: {
          ...body,
          ...ext,
          data: message.data || '',
          type: messageTypes.TYPE_LEAVE_GROUP_CHAT,
        },
      };
    case messageTypes.TYPE_JOINED_GROUP_CHAT:
      return {
        ...obj,
        status: 'sent',
        body: {
          ...body,
          ...ext,
          data: message.data || '',
          type: messageTypes.TYPE_JOINED_GROUP_CHAT,
        },
      };
    case messageTypes.TYPE_INVITE_GROUP_CHAT:
      return {
        ...obj,
        status: 'sent',
        body: {
          ...body,
          ...ext,
          data: message.data,
          type: messageTypes.TYPE_INVITE_GROUP_CHAT,
          senderMemberLevel: message.senderMemberLevel,
          recvMemberLevel: message.recvMemberLevel,
          isRecvMatching: message.isRecvMatching,
          isSenderMatching: message.isSenderMatching,
        },
      };
    case messageTypes.TYPE_INVITE_GROUP:
      return {
        ...obj,
        status: 'sent',
        body: {
          ...body,
          ...ext,
          data: message.data,
          type: messageTypes.TYPE_INVITE_GROUP,
          senderMemberLevel: message.senderMemberLevel,
          recvMemberLevel: message.recvMemberLevel,
          isRecvMatching: message.isRecvMatching,
          isSenderMatching: message.isSenderMatching,
        },
      };
    case messageTypes.TYPE_GIFT:
      return {
        ...obj,
        status: 'sent',
        body: {
          ...body,
          ...ext,
          data: message.data,
          type: messageTypes.TYPE_GIFT,
          senderMemberLevel: message.senderMemberLevel,
          recvMemberLevel: message.recvMemberLevel,
          isRecvMatching: message.isRecvMatching,
          isSenderMatching: message.isSenderMatching,
        },
      };
    case messageTypes.TYPE_VIDEO_FILE:
      let expts = message.expts;
      if (!message.expts) {
        expts = message.createdAt
          ? new Date().getTime() + THREE_MONTHS - (new Date().getTime() - new Date(message.createdAt).getTime())
          : new Date().getTime() + THREE_MONTHS;
      }
      return {
        ...obj,
        status: 'sent',
        expts: expts,
        body: {
          ...body,
          ...ext,
          type: messageTypes.TYPE_VIDEO_FILE,
          senderMemberLevel: message.senderMemberLevel,
          recvMemberLevel: message.recvMemberLevel,
          isRecvMatching: message.isRecvMatching,
          isSenderMatching: message.isSenderMatching,
          expts,
        },
      };
    case messageTypes.TYPE_AUDIO_FILE:
      let exptsAudio = message.expts;
      // if (!message.expts) {
      //   exptsAudio = message.createdAt ? new Date().getTime() + THREE_MONTHS - (new Date().getTime() - new Date(message.createdAt).getTime()) : new Date().getTime() + THREE_MONTHS;
      // }
      return {
        ...obj,
        status: 'sent',
        expts: exptsAudio,
        body: {
          ...body,
          ...ext,
          type: messageTypes.TYPE_AUDIO_FILE,
          senderMemberLevel: message.senderMemberLevel,
          recvMemberLevel: message.recvMemberLevel,
          isRecvMatching: message.isRecvMatching,
          isSenderMatching: message.isSenderMatching,
          expts: exptsAudio,
        },
      };
    // eslint-disable-next-line no-case-declarations
    case messageTypes.TYPE_IMAGE:
      let exp = message.expts;
      if (!message.expts) {
        exp = message.createdAt
          ? new Date().getTime() + THREE_MONTHS - (new Date().getTime() - new Date(message.createdAt).getTime())
          : new Date().getTime() + THREE_MONTHS;
      }
      return {
        ...obj,
        status: 'sent',
        expts: exp,
        body: {
          ...body,
          ...ext,
          type: messageTypes.TYPE_IMAGE,
          senderMemberLevel: message.senderMemberLevel,
          recvMemberLevel: message.recvMemberLevel,
          isRecvMatching: message.isRecvMatching,
          isSenderMatching: message.isSenderMatching,
          expts: exp,
        },
      };
    case messageTypes.TYPE_GALLERY:
      return {
        ...obj,
        status: 'sent',
        body: {
          ...body,
          ...ext,
          type: messageTypes.TYPE_GALLERY,
          senderMemberLevel: message.senderMemberLevel,
          recvMemberLevel: message.recvMemberLevel,
          isRecvMatching: message.isRecvMatching,
          isSenderMatching: message.isSenderMatching,
        },
      };
    case messageTypes.TYPE_VIDEO:
      return {
        ...obj,
        status: 'sent',
        data: message.data.data,
        requestType: message.requestType,
        body: {
          ...body,
          ...ext,
          data: message.data.data,
          type: messageTypes.TYPE_VIDEO,
          senderMemberLevel: message.senderMemberLevel,
          recvMemberLevel: message.recvMemberLevel,
          isRecvMatching: message.isRecvMatching,
          isSenderMatching: message.isSenderMatching,
        },
      };
    case messageTypes.TYPE_LIVE_STREAM:
      return {
        ...obj,
        status: 'sent',
        body: {
          ...body,
          ...ext,
          data: message.data,
          type: messageTypes.TYPE_LIVE_STREAM,
        },
      };
    case messageTypes.TYPE_NOTIFICATION:
      return {
        ...obj,
        status: 'sent',
        body: {
          ...body,
          ...ext,
          data: message.data,
          type: messageTypes.TYPE_NOTIFICATION,
        },
      };
    case messageTypes.TYPE_HEART_MSG:
      return {
        ...obj,
        status: 'sent',
        body: {
          ...body,
          ...ext,
          data: message.data,
          type: messageTypes.TYPE_HEART_MSG,
        },
      };
    case messageTypes.TYPE_LINK:
      return {
        ...obj,
        status: 'sent',
        body: {
          ...body,
          ...ext,
          data: message.data,
          type: messageTypes.TYPE_LINK,
        },
      };
    case messageTypes.TYPE_FASTEN_EDIT:
      return {
        ...obj,
        status: 'sent',
        body: {
          ...body,
          ...ext,
          data: message.data,
          type: messageTypes.TYPE_FASTEN_EDIT,
        },
      };
    default:
      return null;
  }
}

export function parseFromAPIServer(message: ChatMessage, bodyType: string) {
  const ext = message.ext || {};
  const obj = copy(message, msgTpl.base);
  obj.from = removePrefixAndSuffix(message.from || '');
  obj.to = message.type === TYPE_GROUP_CHAT ? message?.to || '' : removePrefixAndSuffix(message.to || '');

  const body = copy(message, msgTpl[bodyType as keyof typeof msgTpl]);
  switch (bodyType) {
    case messageTypes.TYPE_TEXT:
    case messageTypes.TYPE_GALLERY:
    case messageTypes.TYPE_IMAGE:
      return {
        ...obj,
        status: parseXmppStatus(message),
        state: message.state,
        body: {
          ...body,
          ...ext,
          data: message.body?.data,
          type: message.body?.type,
        },
      } as unknown as ChatMessage;
    default:
      break;
  }

  return null;
}

function parseXmppStatus(message: ChatMessage): MessageStatusType {
  if (message.isDelete) {
    return messageStatus.STATUS_DELETED;
  }
  if (message.read) {
    return messageStatus.STATUS_READ;
  }
  if (message.received) {
    return messageStatus.STATUS_DELIVERED;
  }
  if (message.sent) {
    return messageStatus.STATUS_SENT;
  }
  if (!message.sent && !message.received) {
    return messageStatus.STATUS_SENDING;
  }
  return messageStatus.STATUS_SENDING;
}

export function removeJidResource(chatId: string): string {
  if (chatId) {
    return chatId.split('/')[0];
  }
  return '';
}

export function removePrefixAndSuffix(chatId: string | number): string {
  if (chatId) {
    if (typeof chatId === 'number') {
      return chatId.toString();
    }
    if (chatId) {
      const suffix = `@${config.domain}`;
      const prefix = `${config.appkey}_`;
      return chatId.replace(prefix, '').replace(suffix, '');
    }
    return chatId.toString();
  }
  return '';
}

export function addPrefixAndSuffix(chatId: string | number): string {
  let id = chatId;
  if (chatId && !hasPrefixAndSuffix(chatId)) {
    id = removePrefixAndSuffix(chatId);
    const suffix = `@${config.domain}`;
    const prefix = `${config.appkey}_`;
    return prefix + id + suffix;
  }
  return id.toString();
}

export function hasPrefixAndSuffix(xmppId: string | number) {
  const strXmppId = xmppId.toString();
  return strXmppId?.includes(`@${config.domain}`) || strXmppId?.includes(`${config.appkey}_`);
}

export function isGroupMessageStatus(message: ChatMessage) {
  return (
    message?.body?.type === TYPE_JOINED_GROUP_CHAT ||
    message?.body?.type === TYPE_LEAVE_GROUP_CHAT ||
    message?.body?.type === TYPE_REMOVED_FROM_GROUP
  );
}

export function getInboxMessageContent(message: IMessage, peerXmppId: string) {
  if (message && message.body && message.body.type) {
    const iSend = message.type !== TYPE_GROUP_CHAT ? peerXmppId === message.to : message.bySelf;
    if (message.profilePhotoComment?.photoId && message.body.type === messageTypes.TYPE_TEXT) {
      return iSend ? 'Comment sent' : 'Comment received';
    }
    switch (message.body.type) {
      case messageTypes.TYPE_TEXT:
      case messageTypes.TYPE_VIDEO:
        if (message.body.data) {
          return message.body.data?.data?.toString() || message.body.data.toString();
        }
        return '';
      case messageTypes.TYPE_IMAGE:
        const messageObj = { ...message };
        if (messageObj.expts === 0) {
          messageObj.expts = new Date().getTime() + THREE_MONTHS;
        }
        const isImgExpired = message.body.type === messageTypes.TYPE_IMAGE && isImageExpired(messageObj);
        if (isImgExpired) {
          return 'This photo has expired';
        }
        return iSend ? 'Sent photo' : 'Received photo';
      case messageTypes.TYPE_VIDEO_FILE:
        return iSend ? 'Sent video' : 'Received video';
      case messageTypes.TYPE_AUDIO_FILE:
        return iSend ? 'You sent a voice message' : 'Received voice message';
      case messageTypes.TYPE_GIF:
        return iSend ? 'Sent GIF' : 'Received GIF';
      case messageTypes.TYPE_GALLERY:
        return iSend ? 'Sent album' : 'Received album';
      case messageTypes.TYPE_LINK:
        return iSend ? 'Sent link' : 'Received link';
      case messageTypes.TYPE_GIFT:
        return iSend ? 'Sent gift' : 'You have received a gift';
      case messageTypes.TYPE_SHARE_LOCATION:
        return iSend ? 'Sent location' : 'Received location';
      case messageTypes.TYPE_SHARE_DATE:
        return iSend ? 'Sent date' : 'Received date';
      case messageTypes.TYPE_SHARE_EVENT:
        return iSend ? 'Sent event' : 'Received event';
      case messageTypes.TYPE_SHARE_EVENT_TICKET:
        return iSend ? 'Sent event ticket' : 'Received event ticket';
      case messageTypes.TYPE_TOOK_SCREEN_SHOT:
        return 'Screenshot taken';
      case messageTypes.TYPE_INVITE_GROUP_CHAT:
        return 'Group chat invitation';
      case messageTypes.TYPE_INVITE_GROUP:
        return 'Group invitation';
      case messageTypes.TYPE_JOINED_GROUP_CHAT:
      case messageTypes.TYPE_REMOVED_FROM_GROUP:
      case messageTypes.TYPE_LEAVE_GROUP_CHAT:
        return message.body?.data?.value || '';
      default:
        return '';
    }
  }
  return '';
}

export function getPeerXmppId(messageModel: ChatMessage): string {
  if (messageModel) {
    const isGroup = messageModel.type === TYPE_GROUP_CHAT;
    if (isGroup) {
      return messageModel.to?.toString() || '';
    } else if (messageModel.bySelf) {
      return messageModel.to?.toString() || '';
    }
    return messageModel.from?.toString() || '';
  }
  return '';
}

export function getPreviousChatMessageFromId(messages: ChatMessage[], fromId: string) {
  const index = findIndex(messages, (x: any) => {
    return x.id === fromId;
  });

  if (index > 0) {
    for (let i = index - 1; i >= 0; i--) {
      if (!messages[i].isDelete && !messages[i].isHidden) {
        return messages[i];
      }
      if (i === 0 && messages[i].isDelete && !messages[i].isHidden) {
        return null;
      }
    }
  }
  return null;
}

export function isLastConversationMessage(messages: ChatMessage[], byId: string): boolean {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (
      !messages[i].isDelete &&
      !messages[i].isHidden &&
      messages[i].body?.type !== chatStateTypes.COMPOSING &&
      messages[i].body?.type !== chatStateTypes.PAUSED
    ) {
      return messages[i].id === byId;
    }
  }
  return false;
}

export function updateArrMessages(messages: ChatMessage[], chatMessages: ChatMessage[]) {
  chatMessages.forEach(chatMessage => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].id === chatMessage.id) {
        messages[i] = { ...chatMessage, time: messages[i].time };
        break;
      }
    }
  });
  return messages;
}

export function updateMessages(messages: ChatMessage[], chatMessage: ChatMessage): ChatMessage[] {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].id === chatMessage.id) {
      messages[i] = { ...messages[i], ...chatMessage, time: messages[i].time };
      break;
    }
  }
  return messages;
}

export function isPropsMessageActionChanged(oldProps: any, newProps: any) {
  return (
    oldProps.type !== newProps.type ||
    oldProps.updatedTime !== newProps.updatedTime ||
    oldProps.message.id !== newProps.message.id ||
    oldProps.message.status !== newProps.message.status ||
    oldProps.message.state !== newProps.message.state ||
    oldProps.message.isHiddenByError !== newProps.message.isHiddenByError ||
    oldProps.message.hidden !== newProps.message.hidden ||
    oldProps.message.giftOpened !== newProps.message.giftOpened
  );
}

export function isPropsChatStateChanged(peerXmppId: string, oldProps: any, newProps: any) {
  return newProps.byId[peerXmppId] && !shallowequal(newProps.byId[peerXmppId], oldProps.byId[peerXmppId]);
}

export function getXmppMessageById(messageXmppObj: any, msgId: string) {
  return cloneDeep(messageXmppObj.byId[msgId]);
}

export function getMessagesByGalleryIdV2(
  galleryId: string,
  peerXmppId: string,
  msgReducer: IMessageReducer,
  { takeStore }: { takeStore: boolean } = { takeStore: true }
): IMessage | null | undefined | any {
  const messageXmppObj = Object.assign({}, takeStore ? myStore.getStore().getState().messageReducer : msgReducer);
  return Object.values(messageXmppObj.byId).find((m: any) => {
    if (
      m.to === peerXmppId &&
      m.body.data?.galleryId === galleryId &&
      m.body.type === messageTypes.TYPE_GALLERY &&
      m.status !== messageStatus.STATUS_DELETED &&
      m?.status !== messageStatus.STATUS_HIDDEN
    ) {
      return m;
    }
    return null;
  });
}

export function getMessagesByGalleryId(
  galleryId: number,
  peerXmppId: string,
  msgReducer: IMessageReducer,
  { takeStore }: { takeStore: boolean } = { takeStore: true }
) {
  const messages: ChatMessage[] = [];
  const messageXmppObj = Object.assign({}, takeStore ? myStore.getStore().getState().messageReducer : msgReducer);
  if (messageXmppObj) {
    Object.keys(messageXmppObj.byId).forEach(prop => {
      if (
        messageXmppObj.byId[prop].body &&
        messageXmppObj.byId[prop].body.type === messageTypes.TYPE_GALLERY &&
        messageXmppObj.byId[prop].body.data.galleryId === galleryId &&
        messageXmppObj.byId[prop].to === peerXmppId &&
        messageXmppObj.byId[prop].status !== messageStatus.STATUS_DELETED &&
        messageXmppObj.byId[prop].status !== messageStatus.STATUS_HIDDEN
      ) {
        messages.push(cloneDeep(messageXmppObj.byId[prop]));
      }
    });
  }
  return messages;
}

export function sortGroupChatMessages(data: ChatMessage[], revert?: boolean): ChatMessage[] {
  return data.sort((a, b) => {
    return revert ? (b.time || 0) - (a.time || 0) : (a.time || 0) - (b.time || 0);
  });
}

export function getMessageGroupChatByRoom(
  messageXmppObj: IMessageReducer | undefined | null,
  room: Room,
  usersBlockMe: any[],
  usersBlocked: any[],
  isMessageHeart = false,
  isModerator = false,
  advanceParams?: IAdvanceParams
) {
  const { takeStore, revert } = advanceParams || {};
  const messageObj = Object.assign({}, takeStore ? myStore.getStore().getState().messageReducer : messageXmppObj);
  const messages: ChatMessage[] = [];
  let userIdsNotShow: number[] = [];
  if (takeStore) {
    const { usersBlockMe, usersBlocked } = myStore.getStore().getState().usersReducer;
    userIdsNotShow = [...usersBlocked.map((u: any) => u.userId), ...usersBlockMe];
  } else if (usersBlockMe && usersBlocked && (usersBlockMe.length > 0 || usersBlocked.length > 0)) {
    userIdsNotShow = [...usersBlocked.map(u => u.userId), ...usersBlockMe];
  }
  if (messageObj && messageObj.byId && room.roomJid) {
    if (hasChatMessage(messageObj.byId) && messageObj.groupchat[room.roomJid]?.length > 0) {
      const msgIds = new Set(!isMessageHeart ? messageObj.groupchat[room.roomJid] : messageObj.heartMsg[room.roomJid]);
      const loginUser = myStore.getStore().getState().loginReducer.userInfo;
      if (msgIds) {
        msgIds.forEach((msgId: any) => {
          const messageDetail = messageObj.byId[msgId];
          if (messageDetail && (isModerator || userIdsNotShow.indexOf(parseInt(messageDetail.from)) === -1)) {
            const msgModel = convertMsgXmppToChatMessageModel(messageDetail);
            if (msgModel.from === loginUser.xmppId) {
              msgModel.user = loginUser;
            }
            if (msgModel.user.userId && msgModel.user.userId !== -1 && msgModel.user.displayName) {
              if (typeof msgModel.body.data?.toString === 'function') {
                msgModel.body.data = msgModel.body.data.toString();
              }
              messages.push(msgModel);
            }
          }
        });
      }
    }
  }
  if (messages.length > 0) {
    return sortGroupChatMessages(messages, revert);
  }
  return messages;
}

export const hasMessagesUpdated = (oldMessages: ChatMessage[], newMessages: ChatMessage[]) => {
  return (newMessages != null && oldMessages.length !== newMessages.length) || !deepEqual(oldMessages, newMessages);
};

export const sentGroupMessageNotify = (message: IMessage, dispatch: any) => {
  if (!config.isEnableStreamManagementFeature && message.to) {
    const msgType = message.body?.type;
    const dataNotify = msgType === messageTypes.TYPE_IMAGE ? 'Sent a photo' : JSON.stringify(message.body?.data);
    let extraParams: { messageObject: IMessage; mediaType?: string; mediaId?: number } = {
      messageObject: message,
    };
    if (msgType === TYPE_VIDEO_FILE || msgType === TYPE_IMAGE) {
      extraParams = {
        ...extraParams,
        mediaType: msgType === TYPE_VIDEO_FILE ? MEDIA_TYPE.VIDEO : MEDIA_TYPE.PHOTO,
        mediaId: msgType === TYPE_VIDEO_FILE ? message.body?.data.videoId : message.photoId,
      };
    }
    return dispatch(notifyMessageSentGroupChat(dataNotify, message.to, extraParams));
  }
};

export const handleCancelUploadVideo = (message: ChatMessage) => {
  if (message.id && hasOwnProperty(config.videoUploadController, message.id)) {
    let abortList = config.videoUploadController[message.id];
    if (abortList?.length > 0) {
      abortList.forEach((controller: any) => controller?.abort?.());
    }
    if (config.videoUploadController[message.id]) delete config.videoUploadController[message.id];
  }
};

// time valid if differ >= 5 mins
export const checkTimeValid = (timeToReset: string | Date, limitTime: number) => {
  if (timeToReset) {
    const ms = (new Date().getTime() - new Date(timeToReset).getTime()) / (1000 * 60 * 5);
    return ms && ms >= limitTime;
  }
  return true;
};
